import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Upload, Plus, MapPin, Clock, CheckCircle, AlertCircle, Loader2,
  CreditCard, FileText, Building2, Landmark, Shield, FileCheck, Trash2, Eye, Info, Download, Copy, User,
  Crown, Calendar, ArrowRight, Globe, ShoppingBag, Zap
} from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useToast } from '../hooks/use-toast';
import { PincodeInput } from '../components/PincodeInput';

// iter-229g — Detect legacy/bare URLs that point to the container's
// EPHEMERAL local disk. Those files were wiped on each redeploy, so we
// MUST NOT try to fetch them — the request 404s and the UI shows a
// broken image. Return a stable sentinel that the renderer can check.
//
// Anything that:
//   • starts with `/api/uploads/` (legacy local-disk path)
//   • starts with `/uploads/`
//   • is a bare filename (uuid.ext with no scheme + no slashes)
// is considered LOST and triggers the "missing — please re-upload"
// state instead of an actual fetch.
const isLostLegacyKycUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const v = url.trim();
  if (v.startsWith('/api/uploads/')) return true;
  if (v.startsWith('/uploads/')) return true;
  if (!v.includes('://') && !v.startsWith('/') && !v.includes('/')) return true;
  return false;
};

// Document URL resolver — handles ALL legacy storage formats for KYC documents:
//   1. https://...            → absolute, pass through (DO Spaces / CDN)
//   2. /api/uploads/...       → LEGACY, file likely gone → returns null
//   3. /api/files/...         → already correct, prefix with BACKEND_URL
//   4. /uploads/kyc_documents/... → LEGACY, returns null
//   5. uuid.jpg               → bare filename (very old), LEGACY → returns null
//   6. /uuid.jpg              → just a slash + filename, LEGACY → returns null
//
// Returning `null` lets the UI naturally render its "no document" state
// with a re-upload prompt, instead of attempting to fetch a 404'd file.
const resolveDocUrl = (url, backendUrl = '') => {
  if (!url) return null;
  if (isLostLegacyKycUrl(url)) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${backendUrl}${url}`;
  // Partial path that doesn't match legacy — pass through with /api prefix.
  return `${backendUrl}/api/${url.replace(/^\//, '')}`;
};

// Document types configuration
const DOCUMENT_TYPES = [
  { id: 'pan_card', name: 'PAN Card', description: 'Permanent Account Number card for tax identification', required: true, icon: CreditCard },
  { id: 'aadhaar_card', name: 'Aadhaar Card', description: 'Government issued identity proof', required: true, icon: FileCheck },
  { id: 'gst_certificate', name: 'GST Certificate', description: 'Goods and Services Tax registration certificate', required: false, icon: FileText },
  { id: 'msme_certificate', name: 'MSME Certificate', description: 'Micro, Small and Medium Enterprises registration (Udyam)', required: false, icon: Building2 },
  { id: 'bank_statement', name: 'Bank Statement', description: 'Last 3 months bank statement for verification', required: false, icon: Landmark },
  { id: 'cancelled_cheque', name: 'Cancelled Cheque', description: 'For bank account verification and payouts', required: true, icon: CreditCard },
  { id: 'business_license', name: 'Business License', description: 'Shop & Establishment Act or Trade License', required: false, icon: Shield },
  { id: 'address_proof', name: 'Address Proof', description: 'Utility bill, rent agreement, or property documents', required: false, icon: FileText },
  { id: 'brand_authority_letter', name: 'Brand Authority Letter', description: 'Authorization letter from brand owner to sell their products', required: false, icon: FileCheck },
  { id: 'gst_invoices_3months', name: '3 Months GST Invoices', description: 'GST invoices from distributor for the last 3 months', required: false, icon: FileText },
  { id: 'trademark_certificate', name: 'Trademark Certificate', description: 'Registered trademark certificate for your brand', required: false, icon: Shield },
  { id: 'fssai_certificate', name: 'FSSAI Certificate', description: 'Food Safety and Standards Authority of India license', required: false, icon: FileCheck },
  { id: 'shop_establishment', name: 'Shop & Establishment', description: 'Shop and Establishment Act registration certificate', required: false, icon: Building2 },
  { id: 'trade_license', name: 'Trade License', description: 'Trade license issued by local municipal authority', required: false, icon: Shield },
  { id: 'tan_document', name: 'TAN Document', description: 'Tax Deduction and Collection Account Number document', required: false, icon: CreditCard },
  { id: 'iec_license', name: 'IEC License', description: 'Import Export Code license for international trade', required: false, icon: FileText }
];

// Other documents configuration (supports multiple uploads)
const OTHER_DOCUMENTS_CONFIG = {
  id: 'other_documents',
  name: 'Other Documents',
  description: 'Upload any other relevant business documents (multiple files allowed)',
  required: false,
  icon: FileText,
  multiple: true
};

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const { formatPrice } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Get initial tab from URL query parameter
  const initialTab = searchParams.get('tab') || 'general';
  
  // Controlled tab state to prevent jumping
  const [activeTab, setActiveTab] = useState(initialTab);

  // Form states
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [businessDetails, setBusinessDetails] = useState({
    business_name: '',
    legal_name: '',
    business_type: '',
    company_type: '',
    annual_turnover: '',
    is_foreign_national: false,
    gst_number: '',
    enrollment_id: '',
    iec_code: '',
    kyc_level: 'pan_only',
    business_address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const [bankDetails, setBankDetails] = useState({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder_name: ''
  });

  const [contactDetails, setContactDetails] = useState({
    mobile: '',
    support_email: '',
    support_mobile: ''
  });

  const [whatsappDetails, setWhatsappDetails] = useState({
    whatsapp_id: '',
    giri_active: false,
    whatsapp_consent_at: null
  });

  const [documents, setDocuments] = useState({
    pan_card: null,
    aadhaar_card: null,
    gst_certificate: null,
    msme_certificate: null,
    bank_statement: null,
    cancelled_cheque: null,
    business_license: null,
    address_proof: null,
    brand_authority_letter: null,
    gst_invoices_3months: null,
    trademark_certificate: null,
    fssai_certificate: null,
    shop_establishment: null,
    trade_license: null,
    tan_document: null,
    iec_license: null,
    other_documents: [] // Array for multiple uploads
  });

  const loginHistory = JSON.parse(localStorage.getItem('loginHistory') || '[]');
  const userLoginHistory = loginHistory.filter(log => log.userId === user?.id).slice(-10).reverse();

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch vendor profile on mount
  useEffect(() => {
    fetchVendorProfile();
  }, []);

  const fetchVendorProfile = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      // Use new unified profile endpoint
      const response = await fetch(`${BACKEND_URL}/api/vendors/complete-profile/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData(data);

        // Populate personal info from user data
        setPersonalInfo({
          name: data.user?.name || '',
          email: data.user?.email || '',
          phone: data.contact?.primary_mobile || ''
        });

        // Auto-populate business details from tax registration (source of truth)
        if (data.business) {
          // Extract city/pincode from multiple sources
          let city = '';
          let pincode = '';
          let state = '';
          let businessAddress = '';
          
          // Priority 1: Try addresses.business which is now parsed by backend
          if (data.addresses?.business) {
            city = data.addresses.business.city || '';
            pincode = data.addresses.business.pincode || '';
            state = data.addresses.business.state || '';
            businessAddress = data.addresses.business.full_address || '';
          }
          
          // Priority 2: If GST is verified, try to extract from gst_principal_address
          if (data.business.gst_verified && data.business.gst_principal_address) {
            const gstAddr = data.business.gst_principal_address.addr || data.business.gst_principal_address;
            
            if (typeof gstAddr === 'object') {
              // Structured address object
              city = city || gstAddr?.dst || gstAddr?.city || '';
              pincode = pincode || gstAddr?.pncd || gstAddr?.pincode || '';
              state = state || gstAddr?.stcd || gstAddr?.state || '';
              
              // Build full address from parts if not already set
              if (!businessAddress) {
                const parts = [
                  gstAddr.bno, gstAddr.flno, gstAddr.bnm, gstAddr.st,
                  gstAddr.loc, gstAddr.dst, gstAddr.stcd, gstAddr.pncd
                ].filter(Boolean);
                businessAddress = parts.join(', ');
              }
            } else if (typeof gstAddr === 'string') {
              // Single string address - try to parse
              businessAddress = businessAddress || gstAddr;
              
              // Extract pincode (6 digit number)
              if (!pincode) {
                const pincodeMatch = gstAddr.match(/\b(\d{6})\b/);
                if (pincodeMatch) pincode = pincodeMatch[1];
              }
              
              // Try to extract city and state from comma-separated parts
              if (!city || !state) {
                const parts = gstAddr.split(',').map(p => p.trim());
                if (parts.length >= 3) {
                  // Remove pincode if at end
                  let cleanParts = parts.filter(p => !/^\d{6}$/.test(p));
                  if (cleanParts.length >= 2) {
                    state = state || cleanParts[cleanParts.length - 1];
                    city = city || cleanParts[cleanParts.length - 2];
                  }
                }
              }
            }
          }
          
          // Priority 3: Use pickup address as fallback
          if (data.addresses?.pickup && (!city || !state || !pincode)) {
            city = city || data.addresses.pickup.city || '';
            pincode = pincode || data.addresses.pickup.pincode || '';
            state = state || data.addresses.pickup.state || '';
          }
          
          // Determine KYC level based on verified status
          let kycLevel = data.business.compliance_tier || 'pan_only';
          
          // Auto-upgrade KYC level based on verified data
          if (data.business.gst_verified && data.business.gstin) {
            // Check if they also have IEC
            if (data.business.iec_code && data.business.iec_verified) {
              kycLevel = 'gst_iec';
            } else {
              kycLevel = 'gst';
            }
          } else if (data.business.enrollment_verified && data.business.enrollment_id) {
            kycLevel = 'pan_enrollment';
          } else if (data.business.pan_number) {
            kycLevel = 'pan_only';
          }
          
          setBusinessDetails({
            business_name: data.business.trade_name || data.business.business_name || '',
            legal_name: data.business.legal_name || data.business.gst_legal_name || '',
            business_type: data.business.business_type || '',
            company_type: data.business.company_type || '',
            annual_turnover: data.business.annual_turnover || '',
            is_foreign_national: data.business.is_foreign_national || false,
            gst_number: data.business.gstin || '',
            enrollment_id: data.business.enrollment_id || '',
            iec_code: data.business.iec_code || '',
            kyc_level: kycLevel,
            business_address: businessAddress,
            city: city,
            state: state,
            pincode: pincode
          });
        } else {
        }

        // Auto-populate bank details from bank account collection (source of truth)
        if (data.bank) {
          setBankDetails({
            bank_name: data.bank.bank_name || '',
            account_number: data.bank.account_number || '',
            ifsc_code: data.bank.ifsc_code || '',
            account_holder_name: data.bank.account_holder_name || ''
          });
        } else {
        }

        // Auto-populate contact details with fallbacks
        setContactDetails({
          mobile: data.contact?.support_mobile || data.user?.mobile || '',
          support_email: data.contact?.support_email || data.user?.email || '',
          support_mobile: data.contact?.support_mobile || data.user?.mobile || ''
        });

        // Auto-populate WhatsApp details
        if (data.whatsapp) {
          // Convert international format back to 10-digit for display
          let displayNumber = data.whatsapp.whatsapp_id || '';
          if (displayNumber.startsWith('91') && displayNumber.length === 12) {
            displayNumber = displayNumber.substring(2);
          }
          setWhatsappDetails({
            whatsapp_id: displayNumber,
            giri_active: data.whatsapp.giri_active || false,
            whatsapp_consent_at: data.whatsapp.whatsapp_consent_at || null
          });
        }

        // Populate documents from the unified profile response (now includes documents)
        const docs = data.documents || data.vendor_profile?.documents;
        if (docs) {
            setDocuments({
              pan_card: docs.pan_card || null,
              aadhaar_card: docs.aadhaar_card || null,
              gst_certificate: docs.gst_certificate || null,
              msme_certificate: docs.msme_certificate || null,
              bank_statement: docs.bank_statement || null,
              cancelled_cheque: docs.cancelled_cheque || null,
              business_license: docs.business_license || null,
              address_proof: docs.address_proof || null,
              brand_authority_letter: docs.brand_authority_letter || null,
              gst_invoices_3months: docs.gst_invoices_3months || null,
              trademark_certificate: docs.trademark_certificate || null,
              fssai_certificate: docs.fssai_certificate || null,
              shop_establishment: docs.shop_establishment || null,
              trade_license: docs.trade_license || null,
              tan_document: docs.tan_document || null,
              iec_license: docs.iec_license || null,
              other_documents: Array.isArray(docs.other_documents) ? docs.other_documents : []
            });
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePersonalInfo = async () => {
    setSaving(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      const response = await fetch(`${BACKEND_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: personalInfo.name,
          email: personalInfo.email
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Personal information updated successfully'
        });
        fetchVendorProfile();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update personal information',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWhatsappDetails = async () => {
    setSaving(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      // Convert 10-digit to international format
      let whatsappId = whatsappDetails.whatsapp_id || '';
      if (whatsappId && !whatsappId.startsWith('91')) {
        whatsappId = '91' + whatsappId;
      }

      const response = await fetch(`${BACKEND_URL}/api/vendors/whatsapp-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          whatsapp_id: whatsappId,
          giri_active: whatsappDetails.giri_active
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'WhatsApp settings updated successfully'
        });
        await fetchVendorProfile();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update');
      }
    } catch (error) {
      console.error('Error saving WhatsApp details:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update WhatsApp settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };


  const handleSaveBusinessDetails = async () => {
    setSaving(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      const response = await fetch(`${BACKEND_URL}/api/vendors/profile/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          business_details: businessDetails,
          contact_details: contactDetails
        })
      });

      // Also save WhatsApp number if it was entered in Contact Details
      if (whatsappDetails.whatsapp_id) {
        let whatsappId = whatsappDetails.whatsapp_id || '';
        if (whatsappId && !whatsappId.startsWith('91')) {
          whatsappId = '91' + whatsappId;
        }
        await fetch(`${BACKEND_URL}/api/vendors/whatsapp-settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            whatsapp_id: whatsappId,
            giri_active: whatsappDetails.giri_active
          })
        });
      }

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Business details updated successfully'
        });
        fetchVendorProfile();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update business details',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBankDetails = async (e) => {
    if (e) e.preventDefault(); // Prevent default form behavior
    setSaving(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      const response = await fetch(`${BACKEND_URL}/api/vendors/profile/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          bank_details: bankDetails
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Success',
          description: 'Bank details updated successfully'
        });
        await fetchVendorProfile(); // Wait for profile to reload
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update');
      }
    } catch (error) {
      console.error('Error updating bank details:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update bank details',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;

    // Warn user if KYC is verified and they're replacing a document
    const isReplacing = documents[docType] !== null;
    const isKycVerified = profileData?.vendor_profile?.verifications?.gst_verified;
    
    if (isReplacing && isKycVerified) {
      const confirmReplace = window.confirm(
        '⚠️ WARNING: Replacing this document will revoke your KYC verification!\n\n' +
        'Your account will need to be re-verified by the admin, and you will temporarily lose access to booking functionality.\n\n' +
        'Do you want to continue?'
      );
      if (!confirmReplace) return;
    }

    setSaving(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      // Step 1: Upload file
      const formData = new FormData();
      formData.append('files', file);

      const uploadResponse = await fetch(`${BACKEND_URL}/api/files/upload-multiple?category=documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      const uploadData = await uploadResponse.json();
      // The upload endpoint returns an array of objects with {url, filename, ...}
      const fileUrl = uploadData.files[0]?.url || uploadData.files[0];
      
      // Step 2: Update local state
      const updatedDocuments = { ...documents, [docType]: fileUrl };
      setDocuments(updatedDocuments);
      
      // Step 3: Save document URL and revoke KYC if replacing
      const updatePayload = {
        documents: updatedDocuments
      };

      // If replacing and KYC was verified, revoke it
      if (isReplacing && isKycVerified) {
        updatePayload.verifications = {
          gst_verified: false
        };
      }

      const updateResponse = await fetch(`${BACKEND_URL}/api/vendors/profile/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatePayload)
      });

      if (updateResponse.ok) {
        const message = isReplacing && isKycVerified
          ? 'Document replaced successfully. Your KYC verification has been revoked and is pending re-approval.'
          : 'Document uploaded successfully';
        
        toast({
          title: 'Success',
          description: message,
          variant: isReplacing && isKycVerified ? 'default' : 'default'
        });
        
        fetchVendorProfile(); // Refresh profile to show updated KYC status
      } else {
        throw new Error('Failed to save document reference');
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload document',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handler for multiple file uploads (Other Documents)
  const handleOtherDocumentsUpload = async (files) => {
    if (!files || files.length === 0) return;

    setSaving(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      // Upload all files
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });

      const uploadResponse = await fetch(`${BACKEND_URL}/api/files/upload-multiple?category=documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      const uploadData = await uploadResponse.json();
      const newFileUrls = uploadData.files.map(f => ({
        url: f.url || f,
        filename: f.filename || f.url?.split('/').pop() || 'document',
        uploadedAt: new Date().toISOString()
      }));
      
      // Append to existing other_documents
      const existingDocs = Array.isArray(documents.other_documents) ? documents.other_documents : [];
      const updatedOtherDocs = [...existingDocs, ...newFileUrls];
      const updatedDocuments = { ...documents, other_documents: updatedOtherDocs };
      setDocuments(updatedDocuments);
      
      // Save to backend
      const updateResponse = await fetch(`${BACKEND_URL}/api/vendors/profile/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ documents: updatedDocuments })
      });

      if (updateResponse.ok) {
        toast({
          title: 'Success',
          description: `${newFileUrls.length} document(s) uploaded successfully`
        });
        fetchVendorProfile();
      } else {
        throw new Error('Failed to save document references');
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload documents',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handler to remove a single "Other Document"
  const handleRemoveOtherDocument = async (indexToRemove) => {
    const confirmRemove = window.confirm('Are you sure you want to remove this document?');
    if (!confirmRemove) return;

    setSaving(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      const existingDocs = Array.isArray(documents.other_documents) ? documents.other_documents : [];
      const updatedOtherDocs = existingDocs.filter((_, idx) => idx !== indexToRemove);
      const updatedDocuments = { ...documents, other_documents: updatedOtherDocs };
      setDocuments(updatedDocuments);
      
      const updateResponse = await fetch(`${BACKEND_URL}/api/vendors/profile/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ documents: updatedDocuments })
      });

      if (updateResponse.ok) {
        toast({
          title: 'Success',
          description: 'Document removed successfully'
        });
      } else {
        throw new Error('Failed to remove document');
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove document',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // GST certificate mandatory for Tier 3 (gst / gst_iec) vendors
  const isGstTier = ['gst', 'gst_iec'].includes(businessDetails.kyc_level);
  const effectiveDocTypes = DOCUMENT_TYPES.map(d =>
    d.id === 'gst_certificate' && isGstTier ? { ...d, required: true } : d
  );
  const kycRequiredCount = effectiveDocTypes.filter(d => d.required).length;
  const kycRequiredUploaded = effectiveDocTypes.filter(d => d.required && documents[d.id]).length;
  const kycProgressPct = kycRequiredCount > 0 ? Math.round((kycRequiredUploaded / kycRequiredCount) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
            <p className="text-gray-600 mt-2">Manage your vendor profile and business information</p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchVendorProfile}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Refresh Profile
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="overflow-x-auto -mx-2 px-2">
            <TabsList className="inline-flex w-auto min-w-max">
              <TabsTrigger value="general" className="text-xs md:text-sm">General Info</TabsTrigger>
              <TabsTrigger value="business" className="text-xs md:text-sm">Business Details</TabsTrigger>
              <TabsTrigger value="kyc" className="text-xs md:text-sm">KYC & Documents</TabsTrigger>
              <TabsTrigger value="bank" className="text-xs md:text-sm">Bank Details</TabsTrigger>
              <TabsTrigger value="activity" className="text-xs md:text-sm">Activity Log</TabsTrigger>
              {user?.role === 'vendor' && (
                <TabsTrigger value="subscription" data-testid="subscription-tab-trigger" className="text-xs md:text-sm">Subscription</TabsTrigger>
              )}
            </TabsList>
          </div>

          {/* General Info Tab */}
          <TabsContent value="general" className="space-y-6">
            {/* Vendor ID Card - Only show for vendors */}
            {user?.role === 'vendor' && (
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <User className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-xs text-indigo-600 font-medium uppercase tracking-wide">Vendor ID</p>
                        <p className="font-mono text-lg font-semibold text-indigo-900">{user?.aniyor_id || `VEN-${(user?.id || '').slice(0, 6).toUpperCase()}`}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {profileData?.subscription?.plan_name && (
                        <div className="text-right" data-testid="vendor-plan-badge">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Current Plan</p>
                          <Badge className="bg-indigo-600 text-white hover:bg-indigo-700 text-sm px-3 py-0.5">
                            {profileData.subscription.plan_name}
                          </Badge>
                          {profileData.subscription.status && (
                            <p className={`text-xs mt-0.5 ${profileData.subscription.status === 'active' ? 'text-green-600' : 'text-amber-600'}`}>
                              {profileData.subscription.status === 'active' ? 'Active' : profileData.subscription.status}
                            </p>
                          )}
                        </div>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100"
                        onClick={() => {
                          navigator.clipboard.writeText(user?.aniyor_id || user?.id || '');
                          toast({ title: 'Copied', description: 'Vendor ID copied to clipboard' });
                        }}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-24 w-24 rounded-full bg-indigo-100 flex items-center justify-center text-3xl font-bold text-indigo-600">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-lg">{user?.name}</p>
                    <p className="text-sm text-gray-600">{user?.email}</p>
                    <Badge variant="outline" className="mt-1 capitalize">
                      {user?.admin_type === 'super_admin' || user?.role === 'super_admin' 
                        ? 'Super Admin' 
                        : user?.role?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      value={personalInfo.name}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={personalInfo.email}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={personalInfo.phone}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, phone: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                <Button onClick={handleSavePersonalInfo} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Save Changes
                </Button>
              </CardContent>
            </Card>

            {/* WhatsApp Business Settings */}
            {user?.role === 'vendor' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp Business Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="whatsappNumber">Business WhatsApp Number</Label>
                      <Input
                        id="whatsappNumber"
                        type="tel"
                        value={whatsappDetails.whatsapp_id}
                        onChange={(e) => setWhatsappDetails({ ...whatsappDetails, whatsapp_id: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        placeholder="e.g., 9876543210"
                        maxLength={10}
                        data-testid="profile-whatsapp-input"
                      />
                      <p className="text-xs text-gray-500">10-digit number for receiving business alerts via WhatsApp</p>
                    </div>
                    <div className="space-y-2 flex flex-col justify-center">
                      <label className="flex items-center space-x-3 cursor-pointer" data-testid="profile-whatsapp-optin">
                        <input
                          type="checkbox"
                          checked={whatsappDetails.giri_active}
                          onChange={(e) => setWhatsappDetails({ ...whatsappDetails, giri_active: e.target.checked })}
                          className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Enable WhatsApp alerts</span>
                      </label>
                      {whatsappDetails.whatsapp_consent_at && (
                        <p className="text-xs text-gray-500">
                          Opted in on: {new Date(whatsappDetails.whatsapp_consent_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button onClick={handleSaveWhatsappDetails} disabled={saving} data-testid="save-whatsapp-btn">
                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Save WhatsApp Settings
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Business Details Tab */}
          <TabsContent value="business" className="space-y-6">
            {/* Info Banner for Auto-Populated Data - only show if data exists */}
            {profileData?.compliance?.has_completed_tax_registration && profileData?.business && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Business data auto-populated from Tax Registration</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Fields below are automatically filled from your verified tax registration. 
                      You can edit them, but changes to verified data may require re-verification.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Warning Banner - tax registration not complete */}
            {!profileData?.compliance?.has_completed_tax_registration && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-6 w-6 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-yellow-900 text-lg mb-2">⚠️ Tax Registration Not Completed</p>
                    <p className="text-sm text-yellow-800 mb-4">
                      Your business details are empty because you have not completed the tax registration process. 
                      Complete it now to automatically fill all your business information (GST, PAN, business details).
                    </p>
                    <Button 
                      onClick={() => navigate('/vendor/tax-registration')}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      Complete Tax Registration Now →
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <Card>
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="businessName" className="flex items-center gap-2">
                      Trading / Display Name *
                      {businessDetails.business_name && profileData?.business?.gst_verified && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="businessName"
                      value={businessDetails.business_name}
                      onChange={(e) => setBusinessDetails({ ...businessDetails, business_name: e.target.value })}
                      placeholder="Name customers see on the storefront"
                    />
                    <p className="text-xs text-gray-500">This is the name displayed to customers</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="legalName" className="flex items-center gap-2">
                      Legal Registered Name
                      {profileData?.business?.gst_verified && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          GST Verified
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="legalName"
                      value={businessDetails.legal_name}
                      onChange={(e) => setBusinessDetails({ ...businessDetails, legal_name: e.target.value })}
                      placeholder="Registered name as per GST / PAN"
                      disabled={profileData?.business?.gst_verified}
                      className={profileData?.business?.gst_verified ? 'bg-gray-50' : ''}
                    />
                    {profileData?.business?.gst_verified && (
                      <p className="text-xs text-gray-500">Auto-filled from GST verification — contact support to update</p>
                    )}
                  </div>

                  {/* Company Type */}
                  <div className="space-y-2">
                    <Label htmlFor="companyType">Company / Entity Type</Label>
                    <select
                      id="companyType"
                      value={businessDetails.company_type}
                      onChange={(e) => setBusinessDetails({ ...businessDetails, company_type: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="company-type-select"
                    >
                      <option value="">Select entity type</option>
                      <option value="sole_proprietorship">Sole Proprietorship</option>
                      <option value="partnership">Partnership Firm</option>
                      <option value="llp">Limited Liability Partnership (LLP)</option>
                      <option value="private_limited">Private Limited Company (Pvt Ltd)</option>
                      <option value="public_limited">Public Limited Company</option>
                      <option value="opc">One Person Company (OPC)</option>
                      <option value="huf">Hindu Undivided Family (HUF)</option>
                      <option value="trust_ngo">Trust / NGO / Section 8</option>
                      <option value="foreign_entity">Foreign Entity</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Annual Turnover */}
                  <div className="space-y-2">
                    <Label htmlFor="annualTurnover">Annual Turnover (Approx.)</Label>
                    <select
                      id="annualTurnover"
                      value={businessDetails.annual_turnover}
                      onChange={(e) => setBusinessDetails({ ...businessDetails, annual_turnover: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                      data-testid="annual-turnover-select"
                    >
                      <option value="">Select turnover range</option>
                      <option value="lt_20l">Below ₹20 Lakhs</option>
                      <option value="20l_40l">₹20 Lakhs – ₹40 Lakhs</option>
                      <option value="40l_75l">₹40 Lakhs – ₹75 Lakhs</option>
                      <option value="75l_1cr">₹75 Lakhs – ₹1 Crore</option>
                      <option value="1cr_2cr">₹1 Crore – ₹2 Crore</option>
                      <option value="2cr_5cr">₹2 Crore – ₹5 Crore</option>
                      <option value="gt_5cr">Above ₹5 Crore</option>
                    </select>
                    <p className="text-xs text-gray-500">Used to determine applicable GST thresholds and compliance requirements</p>
                  </div>
                </div>

                {/* Foreign National / Entity Toggle */}
                <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <input
                    type="checkbox"
                    id="isForeignNational"
                    checked={businessDetails.is_foreign_national}
                    onChange={(e) => setBusinessDetails({ ...businessDetails, is_foreign_national: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                    data-testid="foreign-national-toggle"
                  />
                  <div>
                    <Label htmlFor="isForeignNational" className="cursor-pointer font-medium">Foreign National / Foreign Entity</Label>
                    <p className="text-xs text-gray-500 mt-0.5">Check this if you are a foreign national or if your business is incorporated outside India</p>
                    {businessDetails.is_foreign_national && (
                      <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
                        Foreign entity / national tax rules (FEMA, FCRA, TDS under DTAA) apply. Our compliance team will reach out for additional documentation and verification.
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gstNumber" className="flex items-center gap-2">
                      GST Number
                      {profileData?.business?.gst_verified && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="gstNumber"
                        value={businessDetails.gst_number}
                        onChange={(e) => setBusinessDetails({ ...businessDetails, gst_number: e.target.value.toUpperCase() })}
                        placeholder="22AAAAA0000A1Z5"
                        disabled={profileData?.business?.gst_verified}
                        className={profileData?.business?.gst_verified ? 'bg-gray-50' : ''}
                      />
                      {!profileData?.business?.gst_verified && businessDetails.gst_number && (
                        <Button 
                          variant="outline"
                          onClick={() => navigate('/vendor/tax-registration?upgrade=gst')}
                          className="whitespace-nowrap"
                        >
                          Verify GST
                        </Button>
                      )}
                      {profileData?.business?.gst_verified && (
                        <Button 
                          variant="outline"
                          onClick={() => {
                            if (window.confirm(
                              '⚠️ Edit GST Number\n\n' +
                              'Changing your verified GST number will:\n' +
                              '• Revoke your current GST verification\n' +
                              '• Require re-verification with the new GSTIN\n' +
                              '• May temporarily affect your ability to sell pan-India\n\n' +
                              'Continue?'
                            )) {
                              navigate('/vendor/tax-registration?edit=gst');
                            }
                          }}
                          className="whitespace-nowrap text-amber-600 border-amber-200 hover:bg-amber-50"
                        >
                          Edit GST
                        </Button>
                      )}
                    </div>
                    {profileData?.business?.gst_verified ? (
                      <p className="text-xs text-green-600">✓ Verified via Government GST Portal</p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        Enter your GSTIN and click &quot;Verify GST&quot; to upgrade your KYC level
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="enrollmentId" className="flex items-center gap-2">
                      Enrollment ID / UID
                      {profileData?.business?.enrollment_verified && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="enrollmentId"
                      value={businessDetails.enrollment_id}
                      onChange={(e) => setBusinessDetails({ ...businessDetails, enrollment_id: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="iecCode" className="flex items-center gap-2">
                      IEC Code (Import/Export)
                      {profileData?.business?.iec_verified && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="iecCode"
                      value={businessDetails.iec_code}
                      onChange={(e) => setBusinessDetails({ ...businessDetails, iec_code: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Selling Capability Level</Label>
                    <div className={`p-3 rounded-lg border-2 ${
                      businessDetails.kyc_level === 'gst_iec' ? 'border-emerald-300 bg-emerald-50' :
                      businessDetails.kyc_level === 'gst' ? 'border-blue-300 bg-blue-50' :
                      businessDetails.kyc_level === 'pan_enrollment' ? 'border-amber-300 bg-amber-50' :
                      'border-gray-300 bg-gray-50'
                    }`} data-testid="selling-capability-level">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                            businessDetails.kyc_level === 'gst_iec' ? 'bg-emerald-200 text-emerald-700' :
                            businessDetails.kyc_level === 'gst' ? 'bg-blue-200 text-blue-700' :
                            businessDetails.kyc_level === 'pan_enrollment' ? 'bg-amber-200 text-amber-700' :
                            'bg-gray-200 text-gray-600'
                          }`}>
                            {businessDetails.kyc_level === 'gst_iec' ? <Globe className="h-5 w-5" /> :
                             businessDetails.kyc_level === 'gst' ? <ShoppingBag className="h-5 w-5" /> :
                             businessDetails.kyc_level === 'pan_enrollment' ? <MapPin className="h-5 w-5" /> :
                             <Zap className="h-5 w-5" />}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">
                              {businessDetails.kyc_level === 'pan_only' && 'Lead Generation Only'}
                              {businessDetails.kyc_level === 'pan_enrollment' && 'Intra-State Sales'}
                              {businessDetails.kyc_level === 'gst' && 'Pan-India Sales'}
                              {businessDetails.kyc_level === 'gst_iec' && 'Global Sales (Import/Export)'}
                              {!businessDetails.kyc_level && 'Not Set'}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {businessDetails.kyc_level === 'pan_only' && 'PAN verified — upgrade by completing GST registration'}
                              {businessDetails.kyc_level === 'pan_enrollment' && 'Enrollment ID verified — sell within your state'}
                              {businessDetails.kyc_level === 'gst' && 'GST verified — sell anywhere in India'}
                              {businessDetails.kyc_level === 'gst_iec' && 'GST + IEC verified — sell globally'}
                              {!businessDetails.kyc_level && 'Complete tax registration to enable selling'}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className={`text-xs ${
                          businessDetails.kyc_level === 'gst_iec' ? 'border-emerald-400 text-emerald-700' :
                          businessDetails.kyc_level === 'gst' ? 'border-blue-400 text-blue-700' :
                          businessDetails.kyc_level === 'pan_enrollment' ? 'border-amber-400 text-amber-700' :
                          'border-gray-400 text-gray-600'
                        }`}>
                          {businessDetails.kyc_level === 'pan_only' && 'Level 1'}
                          {businessDetails.kyc_level === 'pan_enrollment' && 'Level 2'}
                          {businessDetails.kyc_level === 'gst' && 'Level 3'}
                          {businessDetails.kyc_level === 'gst_iec' && 'Level 4'}
                          {!businessDetails.kyc_level && 'N/A'}
                        </Badge>
                      </div>
                      {businessDetails.kyc_level !== 'gst_iec' && (
                        <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            {businessDetails.kyc_level === 'pan_only' && 'Verify your GST to unlock pan-India selling'}
                            {businessDetails.kyc_level === 'pan_enrollment' && 'Register GST to sell across India'}
                            {businessDetails.kyc_level === 'gst' && 'Add IEC to unlock international trade'}
                          </p>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-xs h-auto p-0"
                            onClick={() => navigate('/vendor/tax-registration')}
                          >
                            Upgrade <ArrowRight className="h-3 w-3 ml-1" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {profileData?.business?.gst_verified && (
                      <p className="text-xs text-green-600 mt-1 flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Auto-set based on verified tax registration
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="businessAddress">Business Address *</Label>
                    <Textarea
                      id="businessAddress"
                      value={businessDetails.business_address}
                      onChange={(e) => setBusinessDetails({ ...businessDetails, business_address: e.target.value })}
                      placeholder="Complete business address"
                      rows={3}
                    />
                  </div>
                  <div>
                    <PincodeInput
                      id="pincode"
                      label="Pincode *"
                      value={businessDetails.pincode}
                      onChange={(v) => setBusinessDetails({ ...businessDetails, pincode: v })}
                      onAutoFill={({ city, state }) => {
                        setBusinessDetails(prev => ({
                          ...prev,
                          ...(city ? { city } : {}),
                          ...(state ? { state } : {}),
                        }));
                      }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={businessDetails.city}
                      onChange={(e) => setBusinessDetails({ ...businessDetails, city: e.target.value })}
                      placeholder="Auto-filled from pincode"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={businessDetails.state}
                      onChange={(e) => setBusinessDetails({ ...businessDetails, state: e.target.value })}
                      placeholder="Auto-filled from pincode"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contact Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile Number *</Label>
                    <Input
                      id="mobile"
                      value={contactDetails.mobile}
                      onChange={(e) => setContactDetails({ ...contactDetails, mobile: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactWhatsapp" className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp Business Number
                    </Label>
                    <Input
                      id="contactWhatsapp"
                      data-testid="contact-whatsapp-number"
                      value={whatsappDetails.whatsapp_id}
                      onChange={(e) => setWhatsappDetails({ ...whatsappDetails, whatsapp_id: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                    />
                    <p className="text-xs text-gray-500">Used for customer communication & order updates</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supportEmail">Support Email</Label>
                    <Input
                      id="supportEmail"
                      type="email"
                      value={contactDetails.support_email}
                      onChange={(e) => setContactDetails({ ...contactDetails, support_email: e.target.value })}
                      placeholder="support@business.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supportMobile">Support Mobile</Label>
                    <Input
                      id="supportMobile"
                      value={contactDetails.support_mobile}
                      onChange={(e) => setContactDetails({ ...contactDetails, support_mobile: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                <Button onClick={handleSaveBusinessDetails} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Update Business Details
                </Button>
              </CardContent>
            </Card>

            {/* GST Verification Details Card - Only show if GST verified */}
            {profileData?.business?.gst_verified && (
              <Card className="border-green-200 bg-gradient-to-r from-green-50 to-white">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">GST Verification Details</CardTitle>
                      <p className="text-sm text-gray-600">
                        Verified from Government GST Portal
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Status */}
                    {profileData?.business?.gst_status && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">GST Status</p>
                        <p className={`font-semibold ${profileData.business.gst_status === 'Active' ? 'text-green-700' : 'text-red-600'}`}>
                          {profileData.business.gst_status}
                        </p>
                      </div>
                    )}
                    
                    {/* Taxpayer Type */}
                    {profileData?.business?.gst_taxpayer_type && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Taxpayer Type</p>
                        <p className="font-semibold text-gray-900">{profileData.business.gst_taxpayer_type}</p>
                      </div>
                    )}
                    
                    {/* Constitution */}
                    {profileData?.business?.gst_constitution && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Constitution of Business</p>
                        <p className="font-semibold text-gray-900">{profileData.business.gst_constitution}</p>
                      </div>
                    )}
                    
                    {/* Registration Date */}
                    {profileData?.business?.gst_registration_date && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Registration Date</p>
                        <p className="font-semibold text-gray-900">{profileData.business.gst_registration_date}</p>
                      </div>
                    )}
                    
                    {/* E-Invoice Status */}
                    {profileData?.business?.gst_einvoice_status && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">E-Invoice Status</p>
                        <p className="font-semibold text-gray-900">{profileData.business.gst_einvoice_status}</p>
                      </div>
                    )}
                    
                    {/* Aadhaar Validation */}
                    {profileData?.business?.gst_aadhaar_validated && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Aadhaar Validation</p>
                        <p className={`font-semibold ${profileData.business.gst_aadhaar_validated === 'Yes' ? 'text-green-700' : 'text-gray-900'}`}>
                          {profileData.business.gst_aadhaar_validated}
                          {profileData.business.gst_aadhaar_validation_date && (
                            <span className="text-xs text-gray-500 ml-2">
                              ({profileData.business.gst_aadhaar_validation_date})
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                    
                    {/* eKYC Verified */}
                    {profileData?.business?.gst_ekyc_verified && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">eKYC Verified</p>
                        <p className={`font-semibold ${profileData.business.gst_ekyc_verified === 'Yes' ? 'text-green-700' : 'text-gray-900'}`}>
                          {profileData.business.gst_ekyc_verified}
                        </p>
                      </div>
                    )}
                    
                    {/* Field Visit Conducted */}
                    {profileData?.business?.gst_field_visit_conducted && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Field Visit Conducted</p>
                        <p className="font-semibold text-gray-900">{profileData.business.gst_field_visit_conducted}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Jurisdictions */}
                  {(profileData?.business?.gst_state_jurisdiction || profileData?.business?.gst_center_jurisdiction) && (
                    <div className="mt-4 p-4 bg-white rounded-lg border">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Tax Jurisdictions</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {profileData?.business?.gst_state_jurisdiction && (
                          <div>
                            <p className="text-xs text-gray-400">State Jurisdiction</p>
                            <p className="text-sm font-medium text-gray-900">{profileData.business.gst_state_jurisdiction}</p>
                          </div>
                        )}
                        {profileData?.business?.gst_center_jurisdiction && (
                          <div>
                            <p className="text-xs text-gray-400">Center Jurisdiction</p>
                            <p className="text-sm font-medium text-gray-900">{profileData.business.gst_center_jurisdiction}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Nature of Business Activities */}
                  {profileData?.business?.gst_nature_of_business?.length > 0 && (
                    <div className="mt-4 p-4 bg-white rounded-lg border">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Nature of Business Activities</p>
                      <div className="flex flex-wrap gap-2">
                        {profileData.business.gst_nature_of_business.map((activity, index) => (
                          <Badge key={activity || `activity-${index}`} variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                            {activity}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Bank Details Tab */}
          <TabsContent value="bank" className="space-y-6">
            {/* Info Banner for Bank Data - only show if data exists */}
            {profileData?.compliance?.has_bank_account && profileData?.bank && bankDetails.bank_name && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Bank details auto-populated from Tax Registration</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Your bank account information was verified during tax registration setup. 
                      Changes to verified bank details may require re-verification.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <Card>
              <CardHeader>
                <CardTitle>Bank Account Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bankName" className="flex items-center gap-2">
                      Bank Name *
                      {profileData?.bank?.bank_verified && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="bankName"
                      value={bankDetails.bank_name}
                      onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}
                      placeholder="Enter bank name"
                    />
                    {profileData?.bank?.bank_verified && (
                      <p className="text-xs text-gray-500">Verified via IFSC lookup</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="accountHolder" className="flex items-center gap-2">
                      Account Holder Name *
                      {profileData?.compliance?.has_bank_account && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          Auto-filled
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="accountHolder"
                      value={bankDetails.account_holder_name}
                      onChange={(e) => setBankDetails({ ...bankDetails, account_holder_name: e.target.value })}
                      placeholder="Account holder name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="accountNumber" className="flex items-center gap-2">
                      Account Number *
                      {profileData?.compliance?.has_bank_account && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          Auto-filled
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="accountNumber"
                      value={bankDetails.account_number}
                      onChange={(e) => setBankDetails({ ...bankDetails, account_number: e.target.value })}
                      placeholder="XXXX XXXX XXXX 1234"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ifscCode" className="flex items-center gap-2">
                      IFSC Code *
                      {profileData?.bank?.bank_verified && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="ifscCode"
                      value={bankDetails.ifsc_code}
                      onChange={(e) => setBankDetails({ ...bankDetails, ifsc_code: e.target.value })}
                      placeholder="ABCD0123456"
                    />
                    {profileData?.bank?.bank_verified && (
                      <p className="text-xs text-gray-500">Verified via IFSC portal</p>
                    )}
                  </div>
                </div>
                <Button onClick={handleSaveBankDetails} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Update Bank Details
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* KYC & Documents Tab */}
          <TabsContent value="kyc" className="space-y-6">
            {/* KYC Progress Card */}
            <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-white">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-full">
                      <Shield className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">KYC Verification Progress</h3>
                      <p className="text-sm text-gray-600">
                        {kycRequiredUploaded} of {kycRequiredCount} required documents uploaded
                        {isGstTier && <span className="ml-2 text-amber-700 font-medium">· GST Certificate required for your tier</span>}
                      </p>
                    </div>
                  </div>
                  {profileData?.vendor_profile?.verifications?.gst_verified ? (
                    <Badge variant="default" className="bg-green-600 text-white">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                      <Clock className="h-3 w-3 mr-1" />
                      Pending Review
                    </Badge>
                  )}
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${kycProgressPct}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {kycProgressPct}% complete
                </p>
              </CardContent>
            </Card>

            {/* Info Banner */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Important:</p>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>Documents marked with <span className="text-red-600 font-bold">*</span> are mandatory for verification</li>
                  <li>Accepted formats: PDF, JPG, PNG (max 5MB, Bank Statement max 10MB)</li>
                  <li>All documents must be clear and readable</li>
                  <li>Verification typically takes 1-2 business days</li>
                  {profileData?.vendor_profile?.verifications?.gst_verified && (
                    <li className="text-red-700 font-medium">⚠️ Replacing any document will revoke your KYC approval and require re-verification</li>
                  )}
                </ul>
              </div>
            </div>

            {/* Business Documents Grid */}
            <Card>
              <CardHeader>
                <CardTitle>Business Documents</CardTitle>
                <p className="text-sm text-gray-600">
                  Upload and manage required documents for KYC verification
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {effectiveDocTypes.map((docType) => {
                    const Icon = docType.icon;
                    const hasDoc = documents[docType.id];
                    
                    return (
                      <div 
                        key={docType.id} 
                        className={`border-2 rounded-lg p-4 transition-all ${
                          hasDoc ? 'border-green-200 bg-green-50/30' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${hasDoc ? 'bg-green-100' : 'bg-gray-100'}`}>
                              <Icon className={`h-5 w-5 ${hasDoc ? 'text-green-600' : 'text-gray-600'}`} />
                            </div>
                            <div>
                              <p className="font-medium flex items-center gap-1">
                                {docType.name}
                                {docType.required && <span className="text-red-500">*</span>}
                              </p>
                              <p className="text-xs text-gray-500">{docType.description}</p>
                            </div>
                          </div>
                        </div>

                        {hasDoc ? (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between p-2 bg-white rounded-lg border">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3" /> Uploaded
                                </span>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="View document"
                                  onClick={() => window.open(resolveDocUrl(documents[docType.id], process.env.REACT_APP_BACKEND_URL), '_blank')}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="Download document"
                                  onClick={() => {
                                    // Use the download query param to force download
                                    const rawUrl = documents[docType.id];
                                    const downloadUrl = resolveDocUrl(rawUrl, process.env.REACT_APP_BACKEND_URL) + '?download=true';
                                    const link = document.createElement('a');
                                    link.href = downloadUrl;
                                    link.download = `${docType.name.replace(/\s+/g, '_')}_${Date.now()}`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="Re-upload document"
                                  onClick={() => document.getElementById(`file-${docType.id}`).click()}
                                  disabled={saving}
                                >
                                  <Upload className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <input
                              id={`file-${docType.id}`}
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => handleDocumentUpload(docType.id, e.target.files[0])}
                            />
                          </div>
                        ) : (
                          <div>
                            <label
                              htmlFor={`upload-${docType.id}`}
                              className={`flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                saving ? 'bg-gray-100 border-gray-300' : 'hover:bg-gray-50 hover:border-gray-400 border-gray-300'
                              }`}
                            >
                              <Upload className="h-6 w-6 text-gray-400 mb-2" />
                              <span className="text-sm font-medium text-gray-700">Click to upload</span>
                              <span className="text-xs text-gray-500 mt-1">
                                .pdf, .jpg, .png (max {docType.id === 'bank_statement' ? '10MB' : '5MB'})
                              </span>
                            </label>
                            <input
                              id={`upload-${docType.id}`}
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => handleDocumentUpload(docType.id, e.target.files[0])}
                              disabled={saving}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Other Documents Section - Multiple Uploads */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-blue-600" />
                  Other Documents
                </CardTitle>
                <p className="text-sm text-gray-600">
                  Upload any other relevant business documents. You can upload multiple files.
                </p>
              </CardHeader>
              <CardContent>
                {/* Uploaded Other Documents List */}
                {documents.other_documents && documents.other_documents.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Uploaded Documents ({documents.other_documents.length})
                    </p>
                    {documents.other_documents.map((doc, index) => (
                      <div 
                        key={doc.id || doc.file_name || `doc-${index}`} 
                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {doc.filename || `Document ${index + 1}`}
                            </p>
                            {doc.uploadedAt && (
                              <p className="text-xs text-gray-500">
                                Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View document"
                            onClick={() => window.open(resolveDocUrl(doc.url || doc, process.env.REACT_APP_BACKEND_URL), '_blank')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Download document"
                            onClick={() => {
                              const rawUrl = doc.url || doc;
                              const downloadUrl = resolveDocUrl(rawUrl, process.env.REACT_APP_BACKEND_URL) + '?download=true';
                              const link = document.createElement('a');
                              link.href = downloadUrl;
                              link.download = doc.filename || `other_document_${index + 1}`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Remove document"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveOtherDocument(index)}
                            disabled={saving}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Area for Other Documents */}
                <div>
                  <label
                    htmlFor="upload-other-documents"
                    className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      saving ? 'bg-gray-100 border-gray-300' : 'hover:bg-blue-50 hover:border-blue-400 border-gray-300'
                    }`}
                  >
                    <div className="p-3 bg-blue-100 rounded-full mb-3">
                      <Plus className="h-6 w-6 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">Click to upload documents</span>
                    <span className="text-xs text-gray-500 mt-1">
                      Select multiple files at once • .pdf, .jpg, .png (max 5MB each)
                    </span>
                  </label>
                  <input
                    id="upload-other-documents"
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => handleOtherDocumentsUpload(e.target.files)}
                    disabled={saving}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {userLoginHistory.length > 0 ? (
                  <div className="space-y-3">
                    {userLoginHistory.map((log, index) => (
                      <div key={log.timestamp || `login-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <Clock className="h-5 w-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Login from {log.browser || 'Unknown'}</p>
                            <p className="text-xs text-gray-600">{log.ip || 'Unknown IP'}</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{formatDateTime(log.timestamp)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-center py-8">No activity logs available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          {user?.role === 'vendor' && (
          <TabsContent value="subscription" className="space-y-6" data-testid="subscription-tab-content">
            {profileData?.subscription ? (() => {
              const sub = profileData.subscription;
              const plan = sub.plan || {};
              const isActive = sub.status === 'active';
              const isTrial = sub.status === 'trial';
              const periodEnd = sub.current_period_end ? new Date(sub.current_period_end) : null;
              const periodStart = sub.current_period_start ? new Date(sub.current_period_start) : null;
              const createdAt = sub.created_at ? new Date(sub.created_at) : null;
              // iter-214 — during trial, count-down to trial_end (admin-extended)
              // instead of current_period_end which may have elapsed weeks ago.
              const trialEnd = sub.trial_end ? new Date(sub.trial_end) : null;
              const effectiveEnd = (isTrial && trialEnd) ? trialEnd : periodEnd;
              const daysLeft = effectiveEnd ? Math.max(0, Math.ceil((effectiveEnd - new Date()) / (1000 * 60 * 60 * 24))) : null;
              const trialEndLabel = trialEnd ? trialEnd.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

              return (
                <>
                  {/* iter-214 — Trial banner: ₹0 charged until trial_end */}
                  {isTrial && (
                    <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 p-4" data-testid="trial-banner">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-amber-900">
                            You are on a free trial — ₹0 charged
                            {trialEndLabel ? ` until ${trialEndLabel}` : ''}.
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Full plan features unlocked. Billing starts automatically the day after your trial ends.
                          </p>
                        </div>
                        {trialEndLabel && (
                          <Badge className="bg-amber-600 text-white whitespace-nowrap" data-testid="trial-badge-end">
                            Trial ends {trialEndLabel}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Plan Overview Card */}
                  <Card className={`border-2 ${isActive ? 'border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50' : isTrial ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`h-14 w-14 rounded-xl flex items-center justify-center ${isActive ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                            <Crown className={`h-7 w-7 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-xl font-bold" data-testid="subscription-plan-name">{sub.plan_name || 'Unknown Plan'}</h3>
                              <Badge className={`${isActive ? 'bg-green-100 text-green-800' : isTrial ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-testid="subscription-status">
                                {isActive ? 'Active' : isTrial ? 'Trial' : sub.status?.charAt(0).toUpperCase() + sub.status?.slice(1)}
                              </Badge>
                            </div>
                            {plan.description && <p className="text-sm text-gray-500 mt-1">{plan.description}</p>}
                            {sub.billing_cycle && (
                              <p className="text-xs text-gray-400 mt-1 capitalize" data-testid="subscription-billing-cycle">
                                Billed {sub.billing_cycle}
                              </p>
                            )}
                          </div>
                        </div>
                        {daysLeft !== null && (
                          <div className="text-right" data-testid="subscription-days-left">
                            <p className={`text-2xl font-bold ${daysLeft <= 7 ? 'text-red-600' : daysLeft <= 14 ? 'text-amber-600' : 'text-indigo-600'}`}>
                              {daysLeft}
                            </p>
                            <p className="text-xs text-gray-500">days left</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Billing & Period */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                          <Calendar className="h-4 w-4" /> Billing Period
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">
                            {isTrial ? 'Trial Period' : 'Current Period'}
                          </span>
                          <span className="font-medium" data-testid="subscription-period">
                            {periodStart ? periodStart.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                            {' — '}
                            {isTrial && trialEnd
                              ? trialEnd.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                              : (periodEnd ? periodEnd.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A')}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Subscribed Since</span>
                          <span className="font-medium" data-testid="subscription-since">
                            {createdAt ? createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Billing Cycle</span>
                          <span className="font-medium capitalize" data-testid="subscription-cycle">{sub.billing_cycle || 'N/A'}</span>
                        </div>
                        {sub.cancelled_at && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Cancelled On</span>
                            <span className="font-medium text-red-600">{new Date(sub.cancelled_at).toLocaleDateString('en-IN')}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                          <CreditCard className="h-4 w-4" /> Pricing
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {isTrial && (
                          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="trial-no-charge-note">
                            <strong>TRIAL</strong> — ₹0 charged{trialEndLabel ? ` until ${trialEndLabel}` : ''}.
                            Billing shown below is the plan rate that kicks in after trial ends.
                          </div>
                        )}
                        {plan.monthly_price != null && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Monthly Price</span>
                            <span className="font-medium" data-testid="subscription-monthly-price">
                              {formatPrice(plan.monthly_price, plan.currency || 'INR')}/mo
                            </span>
                          </div>
                        )}
                        {plan.annual_price != null && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Annual Price</span>
                            <span className="font-medium" data-testid="subscription-annual-price">
                              {formatPrice(plan.annual_price, plan.currency || 'INR')}/yr
                            </span>
                          </div>
                        )}
                        {plan.commission_percentage != null && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Commission Rate</span>
                            <span className="font-medium" data-testid="subscription-commission">{plan.commission_percentage}%</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Plan Features */}
                  {plan.name && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500">Plan Includes</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {plan.max_listings != null && (
                            <div className="text-center p-3 rounded-lg bg-gray-50" data-testid="plan-max-listings">
                              <p className="text-xl font-bold text-indigo-600">{plan.max_listings}</p>
                              <p className="text-xs text-gray-500 mt-1">Max Listings</p>
                            </div>
                          )}
                          {plan.max_locations != null && (
                            <div className="text-center p-3 rounded-lg bg-gray-50" data-testid="plan-max-locations">
                              <p className="text-xl font-bold text-indigo-600">{plan.max_locations}</p>
                              <p className="text-xs text-gray-500 mt-1">Max Locations</p>
                            </div>
                          )}
                          {plan.content_limit_mb != null && (
                            <div className="text-center p-3 rounded-lg bg-gray-50" data-testid="plan-storage">
                              <p className="text-xl font-bold text-indigo-600">{plan.content_limit_mb >= 1000 ? `${(plan.content_limit_mb / 1000).toFixed(1)}GB` : `${plan.content_limit_mb}MB`}</p>
                              <p className="text-xs text-gray-500 mt-1">Storage</p>
                            </div>
                          )}
                          {plan.advertisement_credits != null && (
                            <div className="text-center p-3 rounded-lg bg-gray-50" data-testid="plan-ad-credits">
                              <p className="text-xl font-bold text-indigo-600">{plan.advertisement_credits}</p>
                              <p className="text-xs text-gray-500 mt-1">Ad Credits</p>
                            </div>
                          )}
                        </div>
                        {plan.allowed_listing_types && plan.allowed_listing_types.length > 0 && (
                          <div className="mt-4 pt-3 border-t">
                            <p className="text-xs text-gray-500 mb-2">Allowed Listing Types</p>
                            <div className="flex flex-wrap gap-1.5">
                              {plan.allowed_listing_types.map((type) => (
                                <Badge key={type} variant="outline" className="text-xs capitalize">{type}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Manage Plan CTA */}
                  <Card className="border-dashed">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Need more features or listings?</p>
                          <p className="text-xs text-gray-500 mt-0.5">Upgrade your plan or purchase add-ons to scale your business</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/vendor/subscription-plans')}
                          data-testid="manage-subscription-btn"
                        >
                          Manage Plan <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </>
              );
            })() : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Crown className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No Active Subscription</h3>
                  <p className="text-sm text-gray-500 mb-4">Choose a plan to start selling on the marketplace</p>
                  <Button onClick={() => navigate('/vendor/subscription')} data-testid="choose-plan-btn">
                    Choose a Plan <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
